import moment from "moment";
import {
  StoreAuditSummaryRangeDayModel,
  StoreAuditSummaryRangeModel,
  StoreBackstockLedgerDayModel,
  StoreBackstockLedgerModel,
  StoreScratcherReconciliationAuditsRangeModel,
} from "models";
import type { AuditMetricRowData } from "../types/auditMetrics";
import { AuditTone } from "../theme";
import { AuditListCardData } from "../types/auditList";
import {
  AUDIT_DAY_PROGRESS_LABEL,
  buildDayInProgressFooterItems,
  buildDayInProgressMetricRows,
  isAuditDayInProgress,
} from "./auditDayProgress";
import { isLottoSummaryAllZero, isPosSummaryAllZero } from "./auditMissingState";

interface AuditConnectionOptions {
  hasPosType?: boolean;
  isLottoAuthorized?: boolean;
}

const formatNumber = (value: number | null | undefined) => (value == null ? "-" : value.toLocaleString("en-US"));

const formatCurrency = (value: number | null | undefined) =>
  value == null
    ? "-"
    : new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);

const formatRangeDate = (startDate: string, endDate: string) =>
  `${moment(startDate).format("MMM D")} - ${moment(endDate).format("MMM D, YYYY")}`;

const pluralizeAudit = (count: number) => `${count} ${count === 1 ? "Audit" : "Audits"}`;

const formatDayTitle = (reportDate: string, auditCount: number) =>
  `${moment(reportDate).format("ddd, M/D/YYYY")} \u2022 ${pluralizeAudit(auditCount)}`;

const buildVarianceTone = (value: number | null | undefined): AuditTone => {
  if (value == null) {
    return "default";
  }

  if (value > 0) {
    return "positive";
  }

  if (value < 0) {
    return "negative";
  }

  return "matched";
};

const buildVarianceLabel = (value: number) => {
  if (value === 0) {
    return 0;
  }

  const formatted = Math.abs(value).toLocaleString("en-US");
  return value > 0 ? formatted : `-${formatted}`;
};

const buildMetricRow = (
  id: string,
  label: string,
  expected: number | null | undefined,
  reported: number | null | undefined,
  variance: number | null | undefined,
  options?: {
    helperText?: string;
    trailingNote?: string;
    dividerAfter?: "normal" | "strong";
    currency?: boolean;
  }
): AuditMetricRowData => ({
  id,
  label,
  helperText: options?.helperText,
  trailingNote: options?.trailingNote,
  expected: options?.currency ? formatCurrency(expected) : formatNumber(expected),
  reported: options?.currency ? formatCurrency(reported) : formatNumber(reported),
  variance:
    variance == null
      ? "-"
      : options?.currency
        ? variance === 0
          ? 0
          : formatCurrency(variance)
        : buildVarianceLabel(variance),
  tone: buildVarianceTone(variance),
  dividerAfter: options?.dividerAfter,
});

const getTotals = (day?: StoreAuditSummaryRangeDayModel | null) => day?.auditTotals;

const buildSummaryRows = (
  overallTotals: StoreAuditSummaryRangeDayModel,
  connectionOptions?: AuditConnectionOptions
): AuditMetricRowData[] => {
  const totals = getTotals(overallTotals);
  if (!totals?.audit || !totals.pos || !totals.lotto || !totals.reconciliation_variance) {
    return [];
  }

  const hasPosConnection = connectionOptions?.hasPosType !== false;
  const hasLottoConnection = connectionOptions?.isLottoAuthorized !== false;
  const payoutsExpected = totals.lotto.payouts_total + totals.lotto.scratcher_free_tickets_paid_amount;
  const hasScratcherAudit = totals.audit.audit_count > 0;
  const scratcherTotals = hasScratcherAudit
    ? totals.pos_audit_window
    : totals.pos;
  const scratcherReportedCount = scratcherTotals?.scratcher_count;
  const scratcherReportedSales = scratcherTotals?.scratcher_sales;
  const canShowLottoExpected = hasLottoConnection;
  const canShowReported = hasPosConnection;

  return [
    buildMetricRow(
      "summary-lotto",
      "Lotto",
      canShowLottoExpected ? totals.lotto.sales : null,
      canShowReported ? totals.pos.lotto_sales : null,
      totals.reconciliation_variance.lotto_sales_variance,
      {
        currency: true,
      }
    ),
    buildMetricRow(
      "summary-payouts",
      "Payouts",
      canShowLottoExpected ? payoutsExpected : null,
      canShowReported ? totals.pos.payout : null,
      totals.reconciliation_variance.payout_variance,
      {
        currency: true,
        dividerAfter: "strong",
      }
    ),
    buildMetricRow(
      "summary-scratcher-qty",
      "Scratcher Qty",
      hasScratcherAudit ? totals.audit.total_sold : null,
      canShowReported ? scratcherReportedCount : null,
      hasScratcherAudit
        ? totals.reconciliation_variance.scratcher.quantity_variance
        : null
    ),
    buildMetricRow(
      "summary-scratcher-sales",
      "Scratcher $",
      hasScratcherAudit ? totals.audit.total_revenue : null,
      canShowReported ? scratcherReportedSales : null,
      hasScratcherAudit
        ? totals.reconciliation_variance.scratcher.revenue_variance
        : null,
      { currency: true }
    ),
  ];
};

const buildDayRows = (
  day: StoreAuditSummaryRangeDayModel,
  scratcherAuditWindowText = "-"
): AuditMetricRowData[] => {
  const totals = getTotals(day);
  if (!totals?.audit || !totals.pos || !totals.lotto || !totals.reconciliation_variance) {
    return [];
  }

  const payoutsExpected = totals.lotto.payouts_total + totals.lotto.scratcher_free_tickets_paid_amount;
  const hasScratcherAudit = totals.audit.audit_count > 0;

  return [
    buildMetricRow(`${day.reportDate}-lotto`, "Lotto", totals.lotto.sales, totals.pos.lotto_sales, totals.reconciliation_variance.lotto_sales_variance, {
      currency: true,
    }),
    buildMetricRow(`${day.reportDate}-payouts`, "Payouts", payoutsExpected, totals.pos.payout, totals.reconciliation_variance.payout_variance, {
      currency: true,
      dividerAfter: "strong",
      trailingNote: `Scratcher Audit Window: ${scratcherAuditWindowText}`,
    }),
    buildMetricRow(
      `${day.reportDate}-scratcher-qty`,
      "Scratcher Qty",
      hasScratcherAudit ? totals.audit.total_sold : null,
      totals.pos.scratcher_count,
      hasScratcherAudit
        ? totals.reconciliation_variance.scratcher.quantity_variance
        : null
    ),
    buildMetricRow(
      `${day.reportDate}-scratcher-sales`,
      "Scratcher $",
      hasScratcherAudit ? totals.audit.total_revenue : null,
      totals.pos.scratcher_sales,
      hasScratcherAudit
        ? totals.reconciliation_variance.scratcher.revenue_variance
        : null,
      { currency: true }
    ),
  ];
};

const formatBackstockFooterValue = (backstockDay?: StoreBackstockLedgerDayModel | null) => {
  if (!backstockDay) {
    return "-";
  }

  const packs = backstockDay.closingPacks ?? 0;
  const tickets = backstockDay.closingTickets ?? 0;

  if (!packs && !tickets) {
    return "-";
  }

  return `${packs} packs/${tickets} tickets`;
};

const buildFooterStatusItem = (state: "missing" | "in_progress" | "ok" | "na") => {
  if (state === "na") {
    return {
      value: "-",
      tone: "neutral" as const,
    };
  }

  if (state === "missing") {
    return {
      value: "Missing",
      tone: "negative" as AuditTone,
    };
  }

  if (state === "in_progress") {
    return {
      value: "In Progress",
      tone: "positive" as AuditTone,
    };
  }

  return {
    value: "OK",
    tone: "matched" as AuditTone,
  };
};

const buildHeaderVarianceLabel = (value?: number | null) => {
  if (value == null) {
    return undefined;
  }

  if (value === 0) {
    return "Matched";
  }

  return value > 0 ? "Surplus" : "Deficit";
};

const buildHeaderVarianceValue = (value?: number | null) => {
  if (value == null || value === 0) {
    return undefined;
  }

  const prefix = value > 0 ? "+" : "-";
  return `${prefix}${formatCurrency(Math.abs(value))}`;
};

const buildCardStatus = (
  day: StoreAuditSummaryRangeDayModel,
  effectiveDate?: string | null,
  connectionOptions?: AuditConnectionOptions
): Pick<AuditListCardData, "statusLabel" | "statusTone" | "statusKind" | "statusItems" | "headerVarianceLabel" | "headerVarianceValue" | "headerVarianceTone"> => {
  const totals = getTotals(day);
  const auditCount = totals?.audit?.audit_count ?? 0;
  const hasPos = connectionOptions?.hasPosType === false ? true : !isPosSummaryAllZero(totals?.pos);
  const hasLotto = connectionOptions?.isLottoAuthorized === false ? true : !isLottoSummaryAllZero(totals?.lotto);
  const totalVariance = auditCount > 0
    ? totals?.reconciliation_variance?.total_variance
    : null;
  const isBeforeEffectiveDate = Boolean(effectiveDate && moment(day.reportDate).isBefore(moment(effectiveDate), "day"));

  const statusItems: NonNullable<AuditListCardData["statusItems"]> = [];

  if (day.auditMissing) {
    statusItems.push({
      id: "audit",
      label: "Needs Audit",
      tone: "negative",
      kind: "warning",
    });
  }

  const isInProgress = !day.auditMissing && isBeforeEffectiveDate && (!hasPos || !hasLotto);
  if (isInProgress) {
    statusItems.push({
      id: "audit-progress",
      label: "In Progress",
      tone: "positive",
      kind: "hourglass",
    });
  }

  if (!statusItems.length) {
    return {
      headerVarianceLabel: buildHeaderVarianceLabel(totalVariance),
      headerVarianceValue: buildHeaderVarianceValue(totalVariance),
      headerVarianceTone: buildVarianceTone(totalVariance),
    };
  }

  if (statusItems.length === 1) {
    return {
      statusLabel: statusItems[0].label,
      statusTone: statusItems[0].tone,
      statusKind: statusItems[0].kind,
      statusItems,
    };
  }

  return {
    statusLabel: "In Progress",
    statusTone: "positive",
    statusKind: "hourglass",
    statusItems,
  };
};

export const buildAuditListCards = (
  model: StoreAuditSummaryRangeModel,
  backstockLedger?: StoreBackstockLedgerModel | null,
  effectiveDate?: string | null,
  scratcherAuditWindows?: StoreScratcherReconciliationAuditsRangeModel | null,
  connectionOptions?: AuditConnectionOptions
): AuditListCardData[] => {
  const cards: AuditListCardData[] = [];

  if (model.overallTotals) {
    const summaryAuditCount = model.overallTotals.auditTotals.audit?.audit_count ?? 0;
    const summaryVariance = summaryAuditCount > 0
      ? model.overallTotals.auditTotals.reconciliation_variance?.total_variance
      : null;
    const rangeDayCount = moment(model.endDate).diff(moment(model.startDate), "days") + 1;
    cards.push({
      id: "summary",
      variant: "summary",
      title: "Period Overview",
      subtitle: formatRangeDate(model.startDate, model.endDate),
      auditCount: summaryAuditCount,
      averageAuditsPerDay: summaryAuditCount / rangeDayCount,
      rangeDayCount,
      tone: buildVarianceTone(summaryVariance),
      headerVarianceLabel: buildHeaderVarianceLabel(summaryVariance),
      headerVarianceValue: buildHeaderVarianceValue(summaryVariance),
      headerVarianceTone: buildVarianceTone(summaryVariance),
      rows: buildSummaryRows(model.overallTotals, connectionOptions),
    });
  }

  model.dailySummaries.forEach((day) => {
    const totals = getTotals(day);
    const auditCount = totals?.audit?.audit_count ?? 0;
    const totalVariance = auditCount > 0
      ? totals?.reconciliation_variance?.total_variance
      : null;
    const backstockDay = backstockLedger?.getDay(day.reportDate) ?? null;
    const scratcherAuditWindowText = scratcherAuditWindows?.getWindowText(day.reportDate) ?? "-";
    const status = buildCardStatus(day, effectiveDate, connectionOptions);
    const isDayInProgress = isAuditDayInProgress(day.reportDate);
    const isBeforeEffectiveDate = Boolean(effectiveDate && moment(day.reportDate).isBefore(moment(effectiveDate), "day"));
    const lottoFooter = buildFooterStatusItem(
      connectionOptions?.isLottoAuthorized === false
        ? "na"
        : isLottoSummaryAllZero(totals?.lotto)
          ? isBeforeEffectiveDate
            ? "in_progress"
            : "missing"
          : "ok"
    );
    const posFooter = buildFooterStatusItem(
      connectionOptions?.hasPosType === false
        ? "na"
        : isPosSummaryAllZero(totals?.pos)
          ? isBeforeEffectiveDate
            ? "in_progress"
            : "missing"
          : "ok"
    );
    const rows = buildDayRows(day, scratcherAuditWindowText);
    const footerItems = [
      {
        id: `${day.reportDate}-footer-lotto`,
        label: "Lotto",
        value: lottoFooter.value,
        tone: lottoFooter.tone,
      },
      {
        id: `${day.reportDate}-footer-pos`,
        label: "POS",
        value: posFooter.value,
        tone: posFooter.tone,
      },
      {
        id: `${day.reportDate}-footer-backstock`,
        label: "Backstock",
        value: formatBackstockFooterValue(backstockDay),
        tone: "matched" as AuditTone,
      },
    ];

    cards.push({
      id: day.reportDate,
      variant: "date",
      title: formatDayTitle(day.reportDate, auditCount),
      statusLabel: isDayInProgress ? undefined : status.statusLabel,
      statusTone: isDayInProgress ? undefined : status.statusTone,
      statusKind: isDayInProgress ? undefined : status.statusKind,
      statusItems: isDayInProgress ? undefined : status.statusItems,
      headerVarianceLabel: isDayInProgress ? AUDIT_DAY_PROGRESS_LABEL : status.headerVarianceLabel,
      headerVarianceValue: isDayInProgress ? undefined : status.headerVarianceValue,
      headerVarianceTone: isDayInProgress ? "progress" : status.headerVarianceTone,
      tone: isDayInProgress ? "progress" : buildVarianceTone(totalVariance),
      rows: isDayInProgress ? buildDayInProgressMetricRows(rows) : rows,
      footerItems: isDayInProgress ? buildDayInProgressFooterItems(footerItems) : footerItems,
    });
  });

  return cards;
};

export const formatAuditListRangeLabel = (startDate: string, endDate: string) => formatRangeDate(startDate, endDate);
